import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef,
  ViewChild,
  HostBinding,
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
  isTemporary?: boolean
}

@Component({
  selector: 'app-custom-rule-dropdown',
  templateUrl: './custom-rule-dropdown.component.html',
  styleUrls: ['./custom-rule-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomRuleDropdownComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CustomRuleDropdownComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() options: SelectOption[] = []
  @Input() placeholder: string = 'Select option...'
  @Input() allowAddNew: boolean = false
  @Input() addNewPlaceholder: string = 'Type to add new option...'
  @Input() cssClass: string = 'arpio-input full-width'
  @Input() disabled: boolean = false
  @Input() storageKey: string = ''

  @Output() optionAdded = new EventEmitter<SelectOption>()
  @Output() optionRemoved = new EventEmitter<SelectOption>()
  @Output() selectionChanged = new EventEmitter<string>()

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>
  @ViewChild('dropdownPanel') dropdownPanel!: ElementRef<HTMLDivElement>

  @HostBinding('class.dropdown-open') get isDropdownOpen() {
    return this.isOpen
  }

  isOpen = false
  searchTerm = ''
  selectedValue: string = ''
  selectedLabel: string = ''
  filteredOptions: SelectOption[] = []

  private temporaryOptions: SelectOption[] = []
  private onChange = (value: any) => {}
  private onTouched = () => {}

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.loadTemporaryOptions()
    this.updateFilteredOptions()
  }

  ngOnChanges(changes: SimpleChanges) {
    // When options change, update the filtered options and selected label
    if (changes['options']) {
      this.updateFilteredOptions()
      this.updateSelectedLabel()
    }
  }

  ngOnDestroy() {
    // Only save temporary options that are currently selected
    this.cleanupUnusedTemporaryOptions()
    this.saveTemporaryOptions()
  }

  writeValue(value: any): void {
    this.selectedValue = value || ''
    this.updateSelectedLabel()
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  private updateSelectedLabel(): void {
    const allOptions = [...this.options, ...this.temporaryOptions]
    // Try exact match first, then fallback to case-insensitive match for resource groups
    let selectedOption = allOptions.find((opt) => opt.value === this.selectedValue)
    if (!selectedOption && this.selectedValue) {
      selectedOption = allOptions.find((opt) => opt.value.toLowerCase() === this.selectedValue.toLowerCase())
    }
    this.selectedLabel = selectedOption ? selectedOption.label : this.selectedValue || this.placeholder
  }

  private updateFilteredOptions(): void {
    const allOptions = [...this.options, ...this.temporaryOptions]

    if (!this.searchTerm) {
      this.filteredOptions = allOptions
    } else {
      this.filteredOptions = allOptions.filter(
        (option) =>
          option.label.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          option.value.toLowerCase().includes(this.searchTerm.toLowerCase())
      )
    }
  }

  toggleDropdown(): void {
    if (this.disabled) return

    this.isOpen = !this.isOpen
    if (this.isOpen) {
      this.searchTerm = ''
      this.updateFilteredOptions()
      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.nativeElement.focus()
        }
      })
    }
    this.onTouched()
  }

  selectOption(option: SelectOption): void {
    if (option.disabled) return

    this.selectedValue = option.value
    this.selectedLabel = option.label
    this.isOpen = false
    this.searchTerm = ''

    // Don't automatically mark temporary options as permanent
    // They should remain temporary unless explicitly made permanent

    this.onChange(this.selectedValue)
    this.selectionChanged.emit(this.selectedValue)
    this.updateFilteredOptions()
  }

  onSearchInput(event: any): void {
    this.searchTerm = event.target.value
    this.updateFilteredOptions()
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.allowAddNew && this.searchTerm.trim()) {
      event.preventDefault()
      this.addNewOption()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.isOpen = false
      this.searchTerm = ''
    }
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.toggleDropdown()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.isOpen = false
    }
  }

  private addNewOption(): void {
    const newValue = this.searchTerm.trim()
    if (!newValue) return

    const allOptions = [...this.options, ...this.temporaryOptions]
    const existingOption = allOptions.find((opt) => opt.value.toLowerCase() === newValue.toLowerCase())

    if (existingOption) {
      // If option already exists, select it
      this.selectOption(existingOption)
      return
    }

    // Create new temporary option
    const newOption: SelectOption = {
      value: newValue,
      label: newValue,
      isTemporary: true,
    }

    this.temporaryOptions.push(newOption)
    this.saveTemporaryOptions()
    this.selectOption(newOption)
    this.optionAdded.emit(newOption)
  }

  private loadTemporaryOptions(): void {
    if (!this.storageKey) return

    const stored = sessionStorage.getItem(`temp-options-${this.storageKey}`)
    if (stored) {
      try {
        this.temporaryOptions = JSON.parse(stored)
      } catch (e) {
        this.temporaryOptions = []
      }
    }
  }

  private saveTemporaryOptions(): void {
    if (!this.storageKey) return

    sessionStorage.setItem(`temp-options-${this.storageKey}`, JSON.stringify(this.temporaryOptions))
  }

  private cleanupUnusedTemporaryOptions(): void {
    // Only keep temporary options that are currently selected
    this.temporaryOptions = this.temporaryOptions.filter((option) => option.value === this.selectedValue)
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false
      this.searchTerm = ''
    }
  }

  // Helper method to check if we should show "Add new" option
  get showAddNewOption(): boolean {
    if (!this.allowAddNew || !this.searchTerm.trim()) return false

    const allOptions = [...this.options, ...this.temporaryOptions]
    return !allOptions.some((opt) => opt.value.toLowerCase() === this.searchTerm.trim().toLowerCase())
  }

  removeCustomOption(option: SelectOption, event: Event): void {
    event.stopPropagation()
    event.preventDefault()

    if (!option.isTemporary) return

    // Remove from temporary options
    this.temporaryOptions = this.temporaryOptions.filter((opt) => opt.value !== option.value)

    // Update storage
    this.saveTemporaryOptions()

    // If the removed option was currently selected, clear the selection
    if (this.selectedValue === option.value) {
      this.selectedValue = ''
      this.selectedLabel = this.placeholder
      this.onChange('')
      this.selectionChanged.emit('')
    }

    // Update filtered options
    this.updateFilteredOptions()

    // Emit event for parent components to handle
    this.optionRemoved.emit(option)
  }

  // TrackBy function for options to optimize change detection
  optionTrackBy(index: number, option: SelectOption): string {
    return option.value
  }
}
